import { ExclamationCircleIcon } from '@heroicons/react/outline';
import { Input } from 'antd';
import { ParseError, parsePhoneNumberFromString } from 'libphonenumber-js';
import { useEffect, useState } from 'react';
import { useRecoilState } from 'recoil';

import { getGlobalCountry } from '@/atoms/countryState';
import { updateUserPhoneNumber } from '@/services/Live';

import PhonePasswordModal from './PhonePasswordModal';
import ButtonDefault, { ButtonVariants } from '../../shared/basic/button';
import {
  formateErrorAppwriteObject,
  formateErrorObject,
} from '../../shared/formateErrorObject';
import { IntlTelInput } from '../../shared/IntlTelInput';
import { notify } from '../../shared/notify';
import { user } from '../../../atoms/user';
import appwriteService from '../../../config/appwrite';
import { User } from '../../../utils/interfaces/User';

const ProfileForm = () => {
  const [loading, setLoading] = useState(false);
  const [userDataLocal, setUserDataLocal] = useState({
    fullname: '',
    email: '',
    phone: '',
  });
  const [userData, setUserData] = useRecoilState<User | undefined>(user);
  const [phonePasswordModal, setPhonePasswordModal] = useState(false);
  const [phonePasswordModalLoading, setPhonePasswordModalLoading] =
    useState(false);

  const [formData, setFormData] = useState({
    fullname: '',
    email: '',
    phone: '',
    phoneCountryCode: '',
    phonePrefix: '',
  });

  const [errors, setErrors] = useState({
    fullname: '',
    phone: '',
  });

  const [formDataPassword, setFormDataPassword] = useState({
    password: '',
  });

  const [errorsPassword, setErrorsPassword] = useState({
    password: '',
  });
  const globalCountry = getGlobalCountry();

  const getUserProfile = async () => {
    const data = await appwriteService.getUserAccount();

    setUserDataLocal({
      fullname: data?.name || '',
      email: data?.email || '',
      phone: data?.phone,
    });
  };

  useEffect(() => {
    getUserProfile();
  }, []);

  useEffect(() => {
    setFormData({
      fullname: userDataLocal?.fullname || '',
      email: userDataLocal?.email || '',
      phone:
        parsePhoneNumberFromString(userDataLocal?.phone || '')
          ?.nationalNumber || '',
      phoneCountryCode:
        parsePhoneNumberFromString(
          userDataLocal?.phone || '',
        )?.country?.toLowerCase() ||
        globalCountry?.countryCode?.toLowerCase() ||
        'in',
      phonePrefix:
        parsePhoneNumberFromString(userDataLocal?.phone || '')
          ?.countryCallingCode ||
        globalCountry?.phoneCode ||
        '91',
    });
  }, [userDataLocal]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    if (name === 'fullname' && !value.trim()) {
      setErrors({
        ...errors,
        fullname: 'Name is required',
      });
    } else {
      setErrors({
        ...errors,
        fullname: '',
      });
    }
  };

  const handleUpdate = async (data: {
    name?: string;
    email?: string;
    phone?: string;
    password?: string;
    phonePrefix?: string;
    phoneCountryCode?: string;
  }) => {
    try {
      setLoading(true);
      // if (userDataLocal && data?.phone !== userDataLocal.phone) {
      //   setPhonePasswordModal(true);
      //   return;
      // }

      await appwriteService.updateName(data.name as string);
      if (data?.phone && data?.phone !== userDataLocal.phone) {
        await updateUserPhoneNumber({
          phone: data?.phone as string,
          country_code: data?.phonePrefix as string,
        });
        await appwriteService.updateOnboardingPrefs({
          country_code: data?.phonePrefix,
          iso2: data?.phoneCountryCode,
        });
      }
      setUserData((prevUserData) => {
        if (!prevUserData) return undefined;
        return {
          ...prevUserData,
          name: data?.name || '',
          phone: data?.phone || '',
        };
      });

      notify(
        `${data?.password ? 'Password' : 'Profile'} updated successfully!!`,
        'success',
      );
    } catch (error: any) {
      const message = formateErrorObject(error);
      setErrors({
        fullname: '',
        phone: message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneWithPassword = async () => {
    try {
      setPhonePasswordModalLoading(true);
      if (userData?.name !== formData.fullname) {
        await appwriteService.updateName(formData.fullname as string);
      }
      const formateNumber = `+${formData.phonePrefix}${formData?.phone}`;

      await appwriteService.updatePhone(
        formateNumber,
        formDataPassword.password,
      );
      setPhonePasswordModal(false);
      setFormDataPassword({
        password: '',
      });
      notify(`${'Profile'} updated successfully!!`, 'success');
    } catch (error: any) {
      const message = formateErrorAppwriteObject(error);
      setErrorsPassword({
        password: message,
      });
    } finally {
      setPhonePasswordModalLoading(false);
    }
  };

  const handlePhoneInput = ({
    phone,
    phoneCountryCode,
    phonePrefix,
  }: {
    phone: string;
    phoneCountryCode: string;
    phonePrefix: string;
  }) => {
    const cleanedValue = phone.replace(/\D/g, '');

    setFormData({
      ...formData,
      phone: cleanedValue,
      phoneCountryCode,
      phonePrefix,
    });

    try {
      const parsedPhoneNumber = parsePhoneNumberFromString(
        '+' + phonePrefix + cleanedValue,
      );

      if (!cleanedValue.trim()) {
        setErrors({
          ...errors,
          phone: 'Phone number is required',
        });
      } else if (!parsedPhoneNumber) {
        setErrors({
          ...errors,
          phone: 'Invalid phone number format',
        });
      } else if (!parsedPhoneNumber.isValid()) {
        setErrors({
          ...errors,
          phone: 'Invalid phone number. Please check the number and try again.',
        });
      } else {
        setErrors({
          ...errors,
          phone: '',
        });
      }
    } catch (error) {
      if (error instanceof ParseError) {
        setErrors({
          ...errors,
          phone: error.message,
        });
      } else {
        setErrors({
          ...errors,
          phone:
            'An error occurred while validating the phone number. Please try again.',
        });
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Validation
    const validationErrors = {
      fullname: '',
      email: '',
      phone: '',
    };

    if (!formData.fullname) {
      validationErrors.fullname = 'Full name is required';
    }

    if (!formData.phone) {
      validationErrors.phone = 'Phone number is required';
    } else if (!isValidPhone(formData.phone)) {
      validationErrors.phone = 'Enter Valid 10 digit number';
    }

    setErrors(validationErrors);

    if (Object.values(validationErrors).every((error) => !error)) {
      try {
        await handleUpdate({
          name: formData.fullname,
          phone: formData?.phone,
          phoneCountryCode: formData?.phonePrefix,
          phonePrefix: formData?.phonePrefix,
        });
      } catch (error) {
        console.error('Error creating/updating address:', error);
      }
    }
  };

  const isValidPhone = (value: string) => {
    const phoneRegex = /^\d{10}$/;

    return phoneRegex.test(value);
  };

  return (
    <div>
      <div className='flex flex-col gap-4'>
        <div className='col-span-1 flex-col flex gap-y-.5'>
          <label className='text-sm font-medium  md:text-base text-textGrayProfile pb-2 l flex gap-x-2'>
            Name
          </label>
          <Input
            name='fullname'
            value={formData.fullname}
            onChange={handleChange}
            size='large'
            placeholder='Name'
            className=' placeholder:text-textDarkGrayColor rounded-lg border-bgDarkGray  py-2.5  text-white '
          />
          {errors.fullname && <p className='text-red-500'>{errors.fullname}</p>}
        </div>
        <div className='col-span-1 flex-col flex gap-y-.5'>
          <label className='text-sm  font-medium md:text-base text-textGrayProfile pb-2'>
            Email id
            <span className='flex gap-x-1 items-center text-yellow-600 text-xs'>
              <ExclamationCircleIcon className=' w-4' /> This field is not
              editable for security purposes.
            </span>
          </label>

          <Input
            name='email'
            type='email'
            autoComplete='current-email'
            value={formData.email}
            disabled={true}
            readOnly
            size='large'
            placeholder='Email'
            className=' placeholder:text-textDarkGrayColor rounded-lg border-bgDarkGray  py-2.5  text-white '
          />
        </div>
        <div className='col-span-1 flex-col flex gap-y-.5'>
          <label className='text-sm font-medium md:text-base text-textGrayProfile pb-2'>
            Mobile Number
          </label>
          <div className='relative'>
            <IntlTelInput
              phone={formData?.phone || ''}
              phoneCountryCode={
                formData.phoneCountryCode ||
                globalCountry?.countryCode?.toLowerCase() ||
                'in'
              }
              onChange={({ phone, phoneCountryCode, phonePrefix }) => {
                const cleanedPhone = phone.replace(/^0+/, '');
                handlePhoneInput({
                  phone: cleanedPhone,
                  phoneCountryCode,
                  phonePrefix,
                });
              }}
            />
          </div>
          {errors.phone && <p className='text-red-500'>{errors.phone}</p>}
        </div>

        <div className='mt-2 px-1 flex-col flex gap-y-.5'>
          <ButtonDefault
            size={1}
            variant={ButtonVariants.UNSTYLED}
            className='w-full md:w-min p-1 disabled:cursor-not-allowed rounded bg-fadedWhite hover:bg-gray-100 text-black disabled:bg-dropDownMenuBg disabled:text-[#3E3E3E] disabled:border-0'
            loading={loading}
            rounded={false}
            roundedSm={true}
            disabled={
              (userDataLocal.fullname === formData.fullname &&
                userDataLocal.phone ===
                  `+${formData.phonePrefix}${formData.phone}`) ||
              loading ||
              Object.values(errors).some((error) => !!error)
            }
            onClick={handleSubmit}
          >
            <span className='flex text-sm  gap-x-2 px-6 font-semibold whitespace-nowrap  '>
              Update
            </span>
          </ButtonDefault>
        </div>

        <PhonePasswordModal
          visible={phonePasswordModal}
          onCancel={() => setPhonePasswordModal(false)}
          errorsPassword={errorsPassword}
          setFormDataPassword={setFormDataPassword}
          formDataPassword={formDataPassword}
          handlePhoneWithPassword={handlePhoneWithPassword}
          loading={phonePasswordModalLoading}
        />
      </div>
    </div>
  );
};

export default ProfileForm;
